import { Localized } from "@fluent/react"
import {
    Box, Checkbox, Chip, FormControl, FormControlLabel, FormHelperText, Grid, MenuItem,
    TextField, Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import { format, isValid, parseISO } from "date-fns"
import { memo, ReactNode, useCallback, useEffect, useState } from "react"
import { UserPicker, userFullName } from "../../../users/UserPicker"
import { DocumentLanguages } from "../../documentTypes"
import { FormFieldDef, FormFieldValue, FormUserValue, isEmptyValue, localizedText } from "./formSchema"

interface FieldProps {
    field: FormFieldDef
    value: FormFieldValue | undefined
    language: DocumentLanguages
    readOnly: boolean
    onCommit: (value: FormFieldValue) => void
}

/**
 * Help text and the "missing value" marker, stacked. The marker is only a hint: `required` never
 * blocks saving or any workflow step.
 */
const fieldHelper = ({ field, value, language }: FieldProps): ReactNode => {
    const help = localizedText(field.help, language)
    const missing = field.required && isEmptyValue(value)
    if (!help && !missing) return null
    return <>
        {help && <Box component="span" sx={{ display: 'block' }}>{help}</Box>}
        {missing && <Box component="span" sx={{ display: 'block', color: 'warning.dark' }}>
            <Localized id="form-section-missing-value"><span>_Brak wartości</span></Localized>
        </Box>}
    </>
}

/** Text-like fields keep a local draft and commit on blur, so typing does not dispatch per keystroke. */
const TextLikeField = (props: FieldProps) => {
    const { field, value, language, readOnly, onCommit } = props
    const stored = value === null || value === undefined ? '' : String(value)
    const [draft, setDraft] = useState(stored)

    useEffect(() => setDraft(stored), [stored])

    const commit = () => {
        if (draft === stored) return
        if (field.type === 'number') {
            const parsed = draft.trim() === '' ? null : Number(draft)
            onCommit(parsed === null || Number.isNaN(parsed) ? null : parsed)
        } else {
            onCommit(draft)
        }
    }

    return <TextField
        fullWidth
        type={field.type === 'number' ? 'number' : 'text'}
        multiline={field.type === 'textarea'}
        minRows={field.type === 'textarea' ? 3 : undefined}
        label={localizedText(field.label, language)}
        required={field.required}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        InputProps={{ readOnly }}
        helperText={fieldHelper(props)}
    />
}

const DateField = (props: FieldProps) => {
    const { field, value, language, readOnly, onCommit } = props
    const parsed = typeof value === 'string' && value !== '' ? parseISO(value) : null

    return <DatePicker
        label={localizedText(field.label, language)}
        value={parsed}
        readOnly={readOnly}
        onChange={date => {
            if (date === null) onCommit(null)
            else if (isValid(date)) onCommit(format(date, 'yyyy-MM-dd'))
        }}
        slotProps={{ textField: { fullWidth: true, required: field.required, helperText: fieldHelper(props) } }}
    />
}

const SelectField = (props: FieldProps) => {
    const { field, value, language, readOnly, onCommit } = props
    const multiple = field.type === 'multiselect'
    const options = field.options ?? []
    const labelOf = (optionValue: string) =>
        localizedText(options.find(o => o.value === optionValue)?.label, language) || optionValue

    return <TextField
        select
        fullWidth
        label={localizedText(field.label, language)}
        required={field.required}
        value={multiple ? (Array.isArray(value) ? value : []) : (typeof value === 'string' ? value : '')}
        onChange={e => onCommit(e.target.value as string | string[])}
        SelectProps={{
            multiple,
            readOnly,
            renderValue: multiple
                ? selected => <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map(v => <Chip key={v} size="small" label={labelOf(v)} />)}
                </Box>
                : undefined,
        }}
        helperText={fieldHelper(props)}
    >
        {!multiple && !field.required && <MenuItem value=""><em>—</em></MenuItem>}
        {options.map(option =>
            <MenuItem key={option.value} value={option.value}>{localizedText(option.label, language)}</MenuItem>)}
    </TextField>
}

const CheckboxField = (props: FieldProps) => {
    const { field, value, language, readOnly, onCommit } = props
    const helper = fieldHelper(props)

    return <FormControl required={field.required}>
        <FormControlLabel
            label={localizedText(field.label, language) + (field.required ? ' *' : '')}
            control={<Checkbox
                checked={value === true}
                disabled={readOnly}
                onChange={e => onCommit(e.target.checked)}
            />}
        />
        {helper && <FormHelperText sx={{ mt: -0.5 }}>{helper}</FormHelperText>}
    </FormControl>
}

/** Stores the name alongside the id, so a later rename or archived user does not change history. */
const UserField = (props: FieldProps) => {
    const { field, value, language, readOnly, onCommit } = props
    const stored = value !== null && typeof value === 'object' && !Array.isArray(value) ? value as FormUserValue : null

    return <UserPicker
        fullWidth
        value={stored?.userId ?? null}
        onChange={user => onCommit(user ? { userId: user.id, fullName: userFullName(user) } : null)}
        label={localizedText(field.label, language)}
        required={field.required}
        readOnly={readOnly}
        fallbackName={stored?.fullName}
        helperText={fieldHelper(props)}
    />
}

export type FormCommit = (key: string, value: FormFieldValue) => void

/**
 * Memoised, so editing one field does not re-render the others. That holds only while the caller's
 * `onCommit` is stable (a `useCallback`).
 */
const FormField = memo(({ commit, ...rest }: Omit<FieldProps, 'onCommit'> & { commit: FormCommit }) => {
    const { key } = rest.field
    const onCommit = useCallback((value: FormFieldValue) => commit(key, value), [commit, key])
    const props: FieldProps = { ...rest, onCommit }

    switch (props.field.type) {
        case 'heading':
            return <Typography variant="subtitle1" color="primary" sx={{
                fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5, mt: 1,
            }}>
                {localizedText(props.field.label, props.language)}
            </Typography>
        case 'text':
        case 'textarea':
        case 'number':
            return <TextLikeField {...props} />
        case 'date':
            return <DateField {...props} />
        case 'select':
        case 'multiselect':
            return <SelectField {...props} />
        case 'checkbox':
            return <CheckboxField {...props} />
        case 'user':
            return <UserField {...props} />
    }
})

/** Store-agnostic: the section wrapper commits to the slice, the designer preview to local state. */
export const FormSectionView = ({ fields, values, language, readOnly, onCommit }: {
    fields: FormFieldDef[]
    values: Record<string, FormFieldValue>
    language: DocumentLanguages
    readOnly: boolean
    onCommit: FormCommit
}) => {
    return <Grid container spacing={2}>
        {fields.map(field =>
            <Grid item key={field.id} xs={12} md={field.type === 'heading' ? 12 : field.width ?? 12}>
                <FormField
                    commit={onCommit}
                    field={field}
                    value={values[field.key]}
                    language={language}
                    readOnly={readOnly}
                />
            </Grid>)}
    </Grid>
}
